import type { FlowRow, FlowTickerInfo } from 'constants/payloads/realtime';

/**
 * Realtime → query-shape adapter for the flow table (spec §11.3, Phase 3).
 *
 * The worker splits each trade into a FlowRow (trade-level) + FlowTickerInfo
 * (ticker-level) to save bandwidth (D2/D23). This re-joins them into the exact
 * shape `getOptionFlow`/`getFreeOptionFlow` return — the fragment fields plus a
 * nested `tickerDetails` and the `daily_volume` array — so `OptionFlowTable` is
 * untouched.
 *
 * Lives in `utils/shared/trades` (not a screen) so both the OptionFlowPage screen
 * and the FlowPageContainer layout can import it without a layout→screen cycle.
 * FlowPageContainer joins per-row lazily inside its single derive pass; callers that
 * need a materialized array (e.g. TickerResearchPage) use `adaptFlowRow` directly.
 */

// The reassembled row matches `optionFlowAllSidesTickerFields`. Kept loose so it
// slots into the generated GetOptionFlowQuery['option_flow'] type via a cast.
export type AdaptedFlowRow = FlowRow & {
  tickerDetails: { ticker: string; name: string | null; sector: string | null };
  marketCap: number | null;
  dailyVolume: number;
  weeklyTotalSize: number;
  weeklyTotalTrades: number;
  weeklyTotalPremium: number;
  weeklyTotalOpenInterest: number;
  weeklyTotalImpliedVol: number;
  avgContractsPerDay: number;
  avgContractsPerTrade: number;
  avgImpliedVolPerTrade: number;
  avgOpenInterestPerDay: number;
  avgOpenInterestPerTrade: number;
  avgPremiumPerDay: number;
  avgPremiumPerTrade: number;
  avgTradesPerDay: number;
};

export interface DailyVolumeRow {
  ticker: string;
  volume: number;
}

const EMPTY_INFO: Omit<FlowTickerInfo, 'ticker'> = {
  name: null,
  sector: null,
  marketCap: null,
  dailyVolume: 0,
  weeklyTotalSize: 0,
  weeklyTotalTrades: 0,
  weeklyTotalPremium: 0,
  weeklyTotalOpenInterest: 0,
  weeklyTotalImpliedVol: 0,
  avgContractsPerDay: 0,
  avgContractsPerTrade: 0,
  avgImpliedVolPerTrade: 0,
  avgOpenInterestPerDay: 0,
  avgOpenInterestPerTrade: 0,
  avgPremiumPerDay: 0,
  avgPremiumPerTrade: 0,
  avgTradesPerDay: 0,
};

/** Re-join one trade row with its ticker info. */
export function adaptFlowRow(
  row: FlowRow,
  tickers: Record<string, FlowTickerInfo>,
): AdaptedFlowRow {
  const info = tickers[row.ticker] ?? { ticker: row.ticker, ...EMPTY_INFO };
  return {
    ...row,
    tickerDetails: { ticker: info.ticker, name: info.name, sector: info.sector },
    marketCap: info.marketCap,
    dailyVolume: info.dailyVolume,
    weeklyTotalSize: info.weeklyTotalSize,
    weeklyTotalTrades: info.weeklyTotalTrades,
    weeklyTotalPremium: info.weeklyTotalPremium,
    weeklyTotalOpenInterest: info.weeklyTotalOpenInterest,
    weeklyTotalImpliedVol: info.weeklyTotalImpliedVol,
    avgContractsPerDay: info.avgContractsPerDay,
    avgContractsPerTrade: info.avgContractsPerTrade,
    avgImpliedVolPerTrade: info.avgImpliedVolPerTrade,
    avgOpenInterestPerDay: info.avgOpenInterestPerDay,
    avgOpenInterestPerTrade: info.avgOpenInterestPerTrade,
    avgPremiumPerDay: info.avgPremiumPerDay,
    avgPremiumPerTrade: info.avgPremiumPerTrade,
    avgTradesPerDay: info.avgTradesPerDay,
  };
}

/** Derive the daily_volume array from the ticker map (one entry per ticker, not per row). */
export function dailyVolumeFromTickers(tickers: Record<string, FlowTickerInfo>): DailyVolumeRow[] {
  return Object.values(tickers).map((info) => ({
    ticker: info.ticker,
    volume: info.dailyVolume,
  }));
}

/** Re-join a whole row set and derive the daily_volume array from the ticker map. */
export function adaptFlowRows(
  rows: FlowRow[],
  tickers: Record<string, FlowTickerInfo>,
): { flow: AdaptedFlowRow[]; dailyVolume: DailyVolumeRow[] } {
  const flow = rows.map((r) => adaptFlowRow(r, tickers));
  return { flow, dailyVolume: dailyVolumeFromTickers(tickers) };
}

/**
 * Client-side side filter (D2). The paid feed carries every side; the table
 * shows only the selected ones. `activeSides` is the same expanded set the
 * legacy `$tradeSides` GraphQL variable used, so this matches the server `_in`
 * filter exactly. Null ⇒ no filter (free/anon: server already sent ASK only).
 */
export function filterBySides(rows: FlowRow[], activeSides: string[] | null): FlowRow[] {
  if (!activeSides) return rows;
  const set = new Set(activeSides);
  return rows.filter((r) => set.has(r.tradeSide));
}
